"use client"

import { useRef } from "react"
import { Routes, Route } from "react-router-dom"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useGSAP } from "@gsap/react"
import Header from "./components/Header"
import WidgetGrid from "./components/WidgetGrid"
import PartnersSection from "./components/PartnersSection"
import MembershipTabs from "./components/MembershipTabs"
import ReviewCarousel from "./components/ReviewCarousel"
import FAQAccordion from "./components/FAQAccordion"
import ContactForm from "./components/ContactForm"
import Footer from "./components/Footer"
import Login from "./pages/Login"

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger)

// MainContent: giao diện chính ngoài trang login
function MainContent({ containerRef }) {
  return (
    <div className="min-h-screen bg-[#F5E6D3] text-[#000000] overflow-hidden">
      <div ref={containerRef} className="h-screen w-full overflow-y-auto snap-y snap-mandatory">
        <Header />
        <main className="space-y-0">
          {/* 1. Widgets Section */}
          <section id="widgets" className="h-screen w-full flex items-center justify-center snap-start">
            <div className="max-w-6xl mx-auto px-8">
              <WidgetGrid />
            </div>
          </section>

          {/* 2. Partnership Logos */}
          <PartnersSection />

          {/* 3. About Section */}
          <section id="about" className="h-screen w-full flex items-center justify-center snap-start bg-[#F5E6D3]">
            <div className="max-w-5xl mx-auto px-8 flex flex-col md:flex-row items-center gap-12">
              <div className="reveal md:w-1/2">
                <h2 className="text-4xl font-bold mb-6">About Us</h2>
                <p className="text-xl leading-relaxed mb-6">
                  We're passionate about bringing the excitement of dodgeball to everyone. Our mission is to create
                  inclusive, fun, and competitive opportunities for players of all skill levels.
                </p>
                <div className="bg-[#533AFD] text-white p-6 rounded-2xl">
                  <p className="font-medium">
                    "Dodgeball is more than a game - it's community, fitness, and fun combined!"
                  </p>
                </div>
              </div>
              <div className="reveal md:w-1/2">
                <div className="bg-white p-2 rounded-[3rem] shadow-xl">
                  <div className="w-full h-64 md:h-96 bg-gray-200 rounded-[2.5rem] overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop"
                      alt="Dodgeball Team"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 4. Upcoming Events */}
          <section id="events" className="h-screen w-full flex items-center justify-center snap-start bg-[#F5E6D3]">
            <div className="max-w-5xl mx-auto px-8">
              <h2 className="reveal text-4xl font-bold mb-8 text-center">Upcoming Events</h2>
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="reveal h-96 flex items-center justify-center bg-gray-50 rounded-xl">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold mb-4">Interactive Calendar</h3>
                    <p className="text-gray-600">View all upcoming dodgeball events and sessions</p>
                  </div>
                </div>
                <div className="reveal mt-6 flex flex-wrap gap-4 justify-center">
                  <button className="bg-[#E67E22] text-white px-6 py-2 rounded-full hover:bg-[#D35400] transition-colors">
                    All Events
                  </button>
                  <button className="bg-white border border-[#E67E22] text-[#E67E22] px-6 py-2 rounded-full hover:bg-[#E67E22] hover:text-white transition-colors">
                    Leagues
                  </button>
                  <button className="bg-white border border-[#E67E22] text-[#E67E22] px-6 py-2 rounded-full hover:bg-[#E67E22] hover:text-white transition-colors">
                    Tournaments
                  </button>
                  <button className="bg-white border border-[#E67E22] text-[#E67E22] px-6 py-2 rounded-full hover:bg-[#E67E22] hover:text-white transition-colors">
                    Clinics
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* 5. Memberships */}
          <section
            id="memberships"
            className="h-screen w-full flex items-center justify-center snap-start bg-[#F5E6D3]"
          >
            <div className="max-w-5xl mx-auto px-8 w-full">
              <MembershipTabs />
            </div>
          </section>

          {/* 6. Funds & Accessibility */}
          <section id="funds" className="h-screen w-full flex items-center justify-center snap-start bg-[#F5E6D3]">
            <div className="max-w-5xl mx-auto px-8">
              <h2 className="reveal text-4xl font-bold mb-8 text-center">Funds & Accessibility</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="reveal bg-white p-8 rounded-2xl shadow-lg">
                  <h3 className="text-2xl font-bold mb-4 text-[#E67E22]">Adult Fund</h3>
                  <p className="mb-6">
                    Financial support for adult players through the Volwassenenfonds Sport & Cultuur.
                  </p>
                  <button className="bg-[#533AFD] text-white px-6 py-3 rounded-full w-full hover:bg-[#4A32E6] transition-colors">
                    Learn More
                  </button>
                </div>
                <div className="reveal bg-white p-8 rounded-2xl shadow-lg">
                  <h3 className="text-2xl font-bold mb-4 text-[#E67E22]">Youth Fund</h3>
                  <p className="mb-6">Support for young players through the Jeugd Fonds Sport & Cultuur.</p>
                  <button className="bg-[#533AFD] text-white px-6 py-3 rounded-full w-full hover:bg-[#4A32E6] transition-colors">
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* 7. Reviews */}
          <section id="reviews" className="h-screen w-full flex items-center justify-center snap-start bg-[#F5E6D3]">
            <div className="max-w-5xl mx-auto px-8 w-full">
              <ReviewCarousel />
            </div>
          </section>

          {/* 8. FAQ */}
          <section id="faq" className="h-screen w-full flex items-center justify-center snap-start bg-[#F5E6D3]">
            <div className="max-w-5xl mx-auto px-8 w-full">
              <h2 className="text-4xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
              <FAQAccordion />
            </div>
          </section>

          {/* 9. Contact Form */}
          <section id="contact" className="h-screen w-full flex items-center justify-center snap-start bg-[#F5E6D3]">
            <div className="max-w-5xl mx-auto px-8 w-full">
              <ContactForm />
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  )
}

function App() {
  const containerRef = useRef(null)

  useGSAP(
    () => {
      // Section animations
      gsap.utils.toArray("section").forEach((section) => {
        gsap.from(section, {
          opacity: 0,
          y: 50,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            toggleActions: "play none none none",
            invalidateOnRefresh: true,
          },
        })
      })

      gsap.utils.toArray(".reveal").forEach((el, i) => {
        gsap.from(el, {
          opacity: 0,
          y: 40,
          duration: 0.7,
          delay: i * 0.08,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
            toggleActions: "play none none none",
            invalidateOnRefresh: true,
          },
        })
      })

      // Main container animation
      ScrollTrigger.create({
        trigger: containerRef.current,
        start: "top top",
        end: "max",
        pin: true,
        anticipatePin: 1,
        scrub: true,
        invalidateOnRefresh: true,
      })
    },
    { scope: containerRef },
  )

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<MainContent containerRef={containerRef} />} />
    </Routes>
  )
}

export default App
